import { FC } from 'react';
import {
  ActionsMenu,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  TruncatedText,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import { APP, ROUTES } from 'config';
import {
  AdminSuppliersFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { formatCountry, formatList } from 'utils/format';
import { shopItemCategories } from 'types/MarketplaceItems';
import { useSearchParams } from 'utils/navigation';
import {
  COLOR_BY_TAX_EXEMPTION_STATUS,
  taxExemptionStatuses,
} from 'store/shop';
import {
  adminShopItemsFiltersSchema,
  useAdminShopItems,
} from 'store/admin-shop';
import {
  getExemption,
  getExemptionStatus,
  useBulkUpdateTaxExemption,
  useCreateTaxExemption,
  useUpdateTaxExemption,
} from 'store/admin-shop-tax-exemption';
import { UpdateShopItemCategoryModal } from 'containers/admin';
import { useModalContext } from 'store/modal';
import {
  resolveTranslationByLanguagesOrFallback,
  stripHtml,
} from 'utils/general';

interface Props {
  country: string;
}

export const TaxExemptionList: FC<Props> = ({ country }) => {
  const { t, i18n } = useTranslation();
  const { selectedRowIds, setSelectedRowIds } = Table.useSelectedRows();
  const { openModal } = useModalContext();

  const { searchParams: filters, navigate } = useSearchParams(
    adminShopItemsFiltersSchema,
  );

  const { data: items, isLoading } = useAdminShopItems({
    ...filters,
    status: ['active'],
    countries: [country],
    taxExemptionCountries: [country],
  });

  const { mutate: createTaxExemption, isLoading: isCreatingTaxExemption } =
    useCreateTaxExemption();
  const { mutate: updateTaxExemption, isLoading: isUpdatingTaxExemption } =
    useUpdateTaxExemption();
  const {
    mutate: bulkUpdateTaxExemption,
    isLoading: isBulkUpdatingTaxExemption,
  } = useBulkUpdateTaxExemption();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={items?.total} />
            <Stack direction="row" alignItems="center" gap={1}>
              <Filters
                value={filters}
                initialValues={adminShopItemsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                keysToExtractFromTotalCalculation={['search']}
              >
                {({ value: values, initialValues, setFilter }) => (
                  <>
                    <FilterButton
                      label={t('marketplaceItems.supplier')}
                      value={values.suppliers}
                      initialValue={initialValues.suppliers}
                      onChange={(v) => setFilter('suppliers', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <AdminSuppliersFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('benefits.benefit_type')}
                      value={values.categories}
                      initialValue={initialValues.categories}
                      onChange={(v) => setFilter('categories', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="categories"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={shopItemCategories.map((category) => ({
                            key: category,
                            label: t(
                              `marketplaceItems.itemCategory-${category}`,
                            ),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('taxExemptions.taxExempt.title')}
                      value={values.taxExemptionStatus}
                      initialValue={initialValues.taxExemptionStatus}
                      onChange={(v) => setFilter('taxExemptionStatus', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="taxExemptionStatus"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          searchable={false}
                          options={taxExemptionStatuses.map((status) => ({
                            key: status,
                            label: t(`taxExemptions.status.${status}`),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <OpenSearchFilter
                      value={filters.search}
                      onApplyFilter={(v) => setFilter('search', v)}
                      placeholder={t('common.search')}
                    />
                  </>
                )}
              </Filters>
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={items?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('common.name'),
            cell: ({ row: { original: item } }) => (
              <Link
                label={item.name}
                to={ROUTES.admin.shopItems.details.settings.replace(
                  ':id',
                  item.id,
                )}
              />
            ),
          },
          {
            id: 'supplier',
            header: t('marketplaceItems.supplier'),
            cell: ({ row: { original: item } }) => (
              <Link
                label={item.supplier.name}
                to={ROUTES.admin.suppliers.details.replace(
                  ':id',
                  item.supplier.id,
                )}
              />
            ),
          },
          {
            id: 'category',
            header: t('marketplace.filtersItemCategory'),
            cell: ({ row: { original: item } }) =>
              formatList(
                i18n.language,
                item.categories.map((category) =>
                  t(`marketplaceItems.itemCategory-${category}`),
                ),
              ),
          },
          {
            id: 'description',
            header: t('employees.details'),
            cell: ({ row: { original: item } }) => {
              const description = resolveTranslationByLanguagesOrFallback(
                item.translations,
                [i18n.language, country, APP.locales.default],
              )?.description;

              return description ? (
                <TruncatedText text={stripHtml(description)} maxLength={300} />
              ) : null;
            },
          },
          {
            id: 'taxExempt',
            header: t('taxExemptions.taxExempt.title'),
            cell: ({ row: { original: item } }) => {
              const taxExemptionStatus = getExemptionStatus(item, country);
              return (
                <Typography
                  color={COLOR_BY_TAX_EXEMPTION_STATUS[taxExemptionStatus]}
                >
                  {t(`taxExemptions.status.${taxExemptionStatus}`)}
                </Typography>
              );
            },
          },
          {
            id: 'actions',
            cell: ({ row: { original: item } }) => {
              const exemption = getExemption(item, country);

              return (
                <ActionsMenu
                  type="compact"
                  isLoading={isCreatingTaxExemption || isUpdatingTaxExemption}
                  items={[
                    ...(exemption?.status === 'exempt' || !exemption
                      ? [
                          {
                            label: t(
                              'taxExemptions.actions.set_status.non-exempt',
                              {
                                country: formatCountry(i18n.language, country),
                              },
                            ),
                            onClick: () => {
                              if (exemption) {
                                updateTaxExemption({
                                  taxExemptionId: exemption.id,
                                  country,
                                  status: 'non-exempt',
                                });
                              } else {
                                createTaxExemption({
                                  marketplaceItemId: item.id,
                                  taxExemptions: [
                                    { country, status: 'non-exempt' },
                                  ],
                                });
                              }
                            },
                          },
                        ]
                      : []),
                    ...(exemption?.status === 'non-exempt' || !exemption
                      ? [
                          {
                            label: t(
                              'taxExemptions.actions.set_status.exempt',
                              {
                                country: formatCountry(i18n.language, country),
                              },
                            ),
                            onClick: () => {
                              if (exemption) {
                                updateTaxExemption({
                                  taxExemptionId: exemption.id,
                                  country,
                                  status: 'exempt',
                                });
                              } else {
                                createTaxExemption({
                                  marketplaceItemId: item.id,
                                  taxExemptions: [
                                    { country, status: 'exempt' },
                                  ],
                                });
                              }
                            },
                          },
                        ]
                      : []),
                    {
                      label: t('actions.change_benefit_type'),
                      onClick: () => {
                        openModal({
                          component: UpdateShopItemCategoryModal,
                          props: {
                            shopItemId: item.id,
                            initialValues: { categories: item.categories },
                          },
                        });
                      },
                    },
                  ]}
                />
              );
            },
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          items && {
            count: items.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
        selection={{
          value: selectedRowIds,
          onChange: setSelectedRowIds,
          isLoading: isBulkUpdatingTaxExemption,
          actions: taxExemptionStatuses
            .filter((status) => status !== 'undefined')
            .map((status) => ({
              label: t(`taxExemptions.actions.set_status.${status}`, {
                country: formatCountry(i18n.language, country),
              }),
              onClick: () => {
                bulkUpdateTaxExemption(
                  {
                    marketplaceItemIds: Object.keys(selectedRowIds),
                    country,
                    status,
                  },
                  { onSuccess: () => setSelectedRowIds({}) },
                );
              },
            })),
        }}
      />
    </TableWrapper>
  );
};
